import { useEffect, useState } from 'react';
import { TextField } from '../../components/TextField';
import classes from './Preview.module.css';
import { Table } from '../../components/Table/Table';
import { ITableColumn } from '../../components/Table/Table.types';
import { RadioButton } from '../../components/RadioButton';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { changeSelectedTaskId } from '../../redux/appConfigSlice';
import { getContentList } from '../../api/authApisV2';
import { SearchIcon } from '../../assets/icons/SearchIcon';
import { setNextButtonToDisable } from '../../redux/appConfigSlice';

export interface Item {
    taskId: string;
    description: string;
    type: string;
    calibrationStatus: string;
}

export const Preview = () => {
    const {
        testTaker: { testTakerAccessToken },
    } = useSelector((state: RootState) => state.appConfigSlice);
    const dispatch = useDispatch();

    const [list, setList] = useState<Item[]>([]);
    const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [searchInput, setSearchInput] = useState<string>('');

    useEffect(() => {
        if (selectedTaskId !== null) {
            dispatch(changeSelectedTaskId(selectedTaskId));
        }
    }, [dispatch, selectedTaskId]);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        itemsPerPage: 9,
        totalItems: 0,
        totalPages: 0,
    });

    useEffect(() => {
        getPreviewContent();
    }, []);

    const getPreviewContent = async (pageNo?: number, rowCount?: number, taskId?: string ) => {
        setLoading(true);
        const response = await getContentList(testTakerAccessToken, pageNo, rowCount, taskId);
        setList(frameMapping(response.content));
        setPagination({
            currentPage: pageNo || 1,
            itemsPerPage: rowCount || response.size,
            totalItems: response.totalElements,
            totalPages: response.totalPages,
        });
        setLoading(false);
    };


    const handleItemsPerPageChange = (itemsPerPage: number) => {
        getPreviewContent(1, itemsPerPage);
    };

    const handleNextPage = () => {
        getPreviewContent(pagination.currentPage + 1, pagination.itemsPerPage);
    };

    const handlePrevPage = () => {
        getPreviewContent(pagination.currentPage - 1, pagination.itemsPerPage);
    };

    const handleSelectPage = (page: number) => {
        getPreviewContent(page, pagination.itemsPerPage);
        setPagination((prev) => ({ ...prev, currentPage: page }));
    };

    const handleRowSelect = (taskId: string) => {
        setSelectedTaskId(taskId);
    };

    const generatePageNumbers = () => {
        const pages: (number | string)[] = [];

        if (pagination.totalPages <= 6) {
            for (let i = 1; i <= pagination.totalPages; i++) {
                pages.push(i);
            }
        } else if (pagination.currentPage <= 4) {
            for (let i = 1; i <= 5; i++) {
                pages.push(i);
            }
            pages.push('...', pagination.totalPages);
        } else if (pagination.currentPage >= 5 && pagination.currentPage <= pagination.totalPages - 3) {
            pages.push(1, '...', pagination.currentPage - 1, pagination.currentPage, pagination.currentPage + 1, '...', pagination.totalPages);
        } else {
            pages.push(1, '...');
            for (let i = pagination.totalPages - 3; i <= pagination.totalPages; i++) {
                pages.push(i);
            }
        }

        return pages;
    };

    const ItemColumn: ITableColumn<Item>[] = [
        {
            key: 'taskId',
            label: 'Task ID',
            minWidth: '100px',
            width: '200px',
            render: (value, row, index) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <RadioButton
                        id={`task-${index}`}
                        name="task-selection"
                        value={value}
                        checked={selectedTaskId === value}
                        onChange={() => handleRowSelect(value)}
                    />
                    <label htmlFor={`task-${index}`} style={{ cursor: 'pointer' }}>
                        {value}
                    </label>
                </div>
            ),
        },
        {
            key: 'description',
            label: 'Description',
            minWidth: '300px',
        },
        {
            key: 'type',
            label: 'Item Type',
            width: '150px',
        },
        {
            key: 'calibrationStatus',
            label: 'Status',
            width: '150px',
            showStatus: true,
            render: (value) => (
                <span
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '4px 12px 4px 4px',
                        fontSize: '12px',
                        color: value.toLowerCase() === 'live' ? '#027623' : '#333541',
                    }}
                >
                    {value}
                </span>
            ),
        },
    ];

    return (
        <div className={classes.container}>
            <div className={classes.headerContainer}>
                <h2 className={classes.title}>Select one content item to continue</h2>
            </div>

            <div className={classes.bodyContainer}>
                <div className={classes.filterSection}>
                    <label htmlFor="task-search" className={classes.filterLabel}>
                        Filter by Task ID
                    </label>
                    <TextField
                        id="task-search"
                        name="task-search"
                        value={searchInput}
                        onChange={(e) => {
                            setSearchInput(e.target.value)
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                getPreviewContent( 1, pagination.itemsPerPage, searchInput);
                            }
                        }}
                        placeholder="Search items by task ID"
                        className={classes.searchInput}
                        icon={
                            <SearchIcon onClick={() => {getPreviewContent(1, pagination.itemsPerPage, searchInput)}} /> 
                        }     
                    />
                </div>
                <Table
                    columns={ItemColumn}
                    data={list}
                    onRowClick={(data) => {
                        // Deselect row
                        if (data.taskId === selectedTaskId) {
                            setSelectedTaskId(null);
                            dispatch(setNextButtonToDisable(true));
                            return;
                        }

                        setSelectedTaskId(data.taskId);
                        dispatch(setNextButtonToDisable(false));
                    }}
                    loading={loading}
                    Pagination={{
                        onChangeItemsPerPage: handleItemsPerPageChange,
                        itemsPerPage: pagination.itemsPerPage,
                        onNextPage: handleNextPage,
                        totalNumberOfRows: pagination.totalItems,
                        pageNumbers: generatePageNumbers(),
                        currentPage: pagination.currentPage,
                        onPrevPage: handlePrevPage,
                        onSelectPage: handleSelectPage,
                        totalPages: pagination.totalPages,
                    }}
                    selected={selectedTaskId || undefined}
                />
            </div>
        </div>
    );
};

export default Preview;


const frameMapping = (data: Item[]) => {
    return data.map((item: Item) => ({
        ...item,
        type: formatItemType(item.type)
    }));
}

const formatItemType = (type: string): string => {
    const typeMapping: Record<string, string> = {
        'FRAME_A': 'FRAME A',
        'FRAME_BC': 'FRAME B/C',
        'PART_2_3': 'PART 2/3',
    };
    return typeMapping[type] ?? type;
};